import { LinearGradient } from 'expo-linear-gradient';
import { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/index';
import { setEditingAgeRating } from 'store/slices/profileSlice';
import { AgeRatingKey } from 'utils/ageRating';
import { calcSizeForDevice, getResponsiveSize } from 'utils/index';
import { Platform } from 'utils/platform';

import { ProfileRatingSelection } from 'components/Profile/Form/RatingSelection';
import { WebDiv } from 'components/WebDiv';

type ProfileRatingSelectionScreenProps = {
  onClose: () => void;
};

const ProfileRatingSelectionScreen = ({ onClose }: ProfileRatingSelectionScreenProps) => {
  const dispatch = useDispatch();

  const editingProfile = useSelector((state: RootState) => state.profile.editingProfile);

  const isCreateMode = !editingProfile?.id;
  const savedAgeRating = editingProfile?.ageRating ?? '18';

  const [draftAgeRating, setDraftAgeRating] = useState<AgeRatingKey>(savedAgeRating);

  const isHorizontalLayout =
    Platform.isTV || (Platform.OS === 'web' && !Platform.isMobileWeb());

  const handleSave = () => {
    dispatch(setEditingAgeRating(draftAgeRating));
    onClose();
  };

  return (
    <WebDiv
      className="navigation-container navigation-priority"
      style={{ width: '100%', height: '100%' }}>
      <View
        style={
          Platform.isTV
            ? styles.tvContainer
            : isHorizontalLayout
              ? styles.webDesktopContainer
              : styles.webContainer
        }>
        <Text style={[styles.title, Platform.isTV ? styles.tvTitle : styles.webTitle]}>
          Classificação etária
        </Text>
        <Text style={styles.introText}>
          Jornalismo e Esportes não têm restrição etária e serão exibidos em qualquer faixa.
        </Text>
        <Text style={styles.selectionTitle}>
          Selecione abaixo a classificação indicativa para este perfil:
        </Text>
        <ProfileRatingSelection
          selectedRating={draftAgeRating}
          baseRating={savedAgeRating}
          onSelect={setDraftAgeRating}
          isCreateMode={isCreateMode}
        />
        <View
          style={[
            styles.actionsBtn,
            Platform.isTV || isHorizontalLayout
              ? styles.tvActionsBtn
              : styles.webActionsBtn,
            isHorizontalLayout && styles.horizontalActionsBtnCenter,
          ]}>
          <WebDiv className="rounded-6">
            <Pressable
              accessibilityRole="button"
              onPress={handleSave}
              style={
                Platform.isTV || isHorizontalLayout
                  ? styles.tvSaveBtnWrapper
                  : styles.webSaveBtnWrapper
              }>
              <LinearGradient
                colors={['#00AD5C', '#6DFFBC']}
                start={{ x: 0, y: 1 }}
                end={{ x: 0, y: 0 }}
                style={[
                  styles.saveBtn,
                  Platform.isTV || isHorizontalLayout
                    ? styles.tvSaveBtn
                    : styles.webSaveBtn,
                ]}>
                <Text style={styles.saveBtnText}>Salvar definição</Text>
              </LinearGradient>
            </Pressable>
          </WebDiv>
          <Pressable
            accessibilityRole="button"
            style={[
              styles.cancelBtn,
              Platform.isTV || isHorizontalLayout
                ? styles.tvCancelBtn
                : styles.webCancelBtn,
            ]}
            onPress={onClose}>
            <Text style={styles.cancelBtnText}>Cancelar</Text>
          </Pressable>
        </View>
      </View>
    </WebDiv>
  );
};

const styles = StyleSheet.create({
  tvContainer: {
    maxWidth: calcSizeForDevice(1510),
    marginTop: calcSizeForDevice(71),
    marginHorizontal: 'auto',
  },
  webContainer: {
    width: '90%',
    maxWidth: calcSizeForDevice(540),
    marginTop: Number(getResponsiveSize({ base: 16, md: 71 })),
    alignSelf: 'center',
    overflow: 'scroll',
    paddingBottom: 32,
  },
  webDesktopContainer: {
    width: '90%',
    maxWidth: calcSizeForDevice(1510),
    marginTop: Number(getResponsiveSize({ base: 16, md: 71 })),
    marginHorizontal: 'auto',
    alignSelf: 'center',
    paddingBottom: 32,
  },
  title: {
    fontWeight: '600',
    color: '#7A8CFF',
    marginBottom: calcSizeForDevice(16),
  },
  tvTitle: {
    fontSize: calcSizeForDevice(36),
  },
  webTitle: {
    fontSize: Number(getResponsiveSize({ base: 28, md: 36 })),
  },
  introText: {
    fontSize: calcSizeForDevice(16),
    color: '#FFF',
    marginBottom: calcSizeForDevice(16),
  },
  selectionTitle: {
    fontSize: calcSizeForDevice(16),
    fontWeight: '600',
    color: '#FFF',
    marginBottom: calcSizeForDevice(30),
  },
  actionsBtn: {
    marginTop: calcSizeForDevice(24),
  },
  tvActionsBtn: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  horizontalActionsBtnCenter: {
    justifyContent: 'center',
  },
  webActionsBtn: {
    flexDirection: getResponsiveSize({ base: 'column', md: 'row' }),
    justifyContent: 'center',
    width: '100%',
  },
  tvSaveBtnWrapper: {
    marginRight: calcSizeForDevice(24),
  },
  webSaveBtnWrapper: {
    flex: 1,
    marginRight: Number(getResponsiveSize({ base: 0, md: 24 })),
  },
  saveBtn: {
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tvSaveBtn: {
    height: calcSizeForDevice(54),
    paddingHorizontal: calcSizeForDevice(32),
  },
  webSaveBtn: {
    width: '100%',
    maxWidth: 366,
    height: 54,
    paddingHorizontal: calcSizeForDevice(24),
  },
  saveBtnText: {
    fontSize: calcSizeForDevice(20),
    fontWeight: '600',
    color: '#070D31',
  },
  cancelBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    height: calcSizeForDevice(54),
  },
  tvCancelBtn: {
    width: calcSizeForDevice(140),
    paddingVertical: 'auto',
  },
  webCancelBtn: {
    marginTop: Number(getResponsiveSize({ base: 24, md: 0 })),
    width: getResponsiveSize({ base: '100%', md: 130 }),
  },
  cancelBtnText: {
    fontSize: calcSizeForDevice(20),
    fontWeight: '600',
    color: '#A8B4FF',
  },
});

export default ProfileRatingSelectionScreen;
